import NiceModal from '@ebay/nice-modal-react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';

import LogoIsbasiModal from 'pages/OtherIntegrations/Integrations/Modals/LogoIsbasi/LogoIsbasiModal';

import PATHS from 'utils/paths';

import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';

import * as S from './IntegrationCard.styled';
import IntegrationDynamicForm from './Modals/IntegrationDynamicForm';
import UpgradeEnterprise from './Modals/UpgradeEnterprise';
import WebHooksModal from './Modals/WebHooksModal';
import WhatsappBusinessModal from './Modals/WhatsappBusiness/WhatsappBusinessModal';

/**
 *
 * @author @muhammedburakak
 */

const colorMapping = {
	wms: '#C11D6F',
	dms: '#1D62C1',
	erp: '#E24343',
	sms: '#1DC185',
	webhooks: '#1DC185'
};
const Card = ({
	title = 'None',
	logo,
	skeleton = false,
	fieldNames,
	content = 'defaultIntegrationsDescription',
	integrationID,
	badge = '',
	keyString = null
}) => {
	const { t } = useTranslation();
	const isAuthorized = useAccessControl();
	const { showError, showInfo } = useAlert();
	const navigate = useNavigate();

	const currentPlan = useSelector((state) => state.user.planName);
	const onClickFunction = () => {
		const modalProps = {
			fieldNames,
			logo,
			title,
			content,
			integrationID
		};
		if (keyString === 'whatsappBusiness') {
			return isAuthorized('EmailAndWhatsappModule')
				? { component: WhatsappBusinessModal, modalProps }
				: (showInfo({
						header: 'info.pleaseOpenEmailAndWhatsappModuleTitle',
						message: 'info.pleaseOpenEmailAndWhatsappModuleMessage'
					}),
					navigate(PATHS.OTO_MODULES));
		} else if (keyString === 'otoWebhook') {
			return { component: WebHooksModal, modalProps };
		} else if (badge === 'sms') {
			return isAuthorized('SMSModule')
				? { component: IntegrationDynamicForm, modalProps }
				: (showInfo({
						header: 'info.pleaseOpenSMSModuleTitle',
						message: 'info.pleaseOpenSMSModuleMessage'
					}),
					navigate(PATHS.OTO_MODULES));
		} else if (keyString === 'logoIsbasi') {
			return { component: LogoIsbasiModal, modalProps };
		} else {
			return { component: UpgradeEnterprise, modalProps };
		}
	};

	return (
		<>
			<S.Card
				onClick={() => {
					const { component, modalProps } = onClickFunction();

					if (
						keyString === 'whatsappBusiness' &&
						(currentPlan === 'FreePackage' || currentPlan === 'StarterPackage')
					) {
						showError({
							header: 'info.limitedWhatsappModuleTitle',
							message: 'info.limitedWhatsappModuleDescription'
						});
						return;
					}

					skeleton === false ? NiceModal.show(component, modalProps) : undefined;
				}}
				style={{ display: 'grid' }}>
				<Button
					data-id={`${title}_Button`}
					variant="borderless"
					style={{ border: '1px solid var(--border-color)', width: 'unset' }}>
					<div style={{ display: 'flex', width: '100%' }}>
						<S.ImgContainer>
							{skeleton ? (
								<Skeleton height="9rem" width="9rem" />
							) : (
								<Image
									url={logo}
									height="9rem"
									width="9rem"
									style={{ objectFit: 'scale-down', radius: '4px' }}
								/>
							)}
						</S.ImgContainer>
						<S.RightCol>
							<div>
								<S.TitleContainer>
									<Typography
										css={skeleton !== true && S.TitleCss}
										fontWeight="700"
										noWrap
										className="mt-2">
										{skeleton ? <Skeleton width="8rem" height="1.5rem" /> : t(title)}
									</Typography>
									<Typography
										css={skeleton !== true && S.BadgeCss}
										noWrap
										style={{ background: colorMapping[badge] }}>
										{skeleton ? <Skeleton width="3rem" height="1.5rem" /> : t(badge)}
									</Typography>
								</S.TitleContainer>
								<S.Content>
									<Typography
										variant="caption"
										color="var(--content-3) !important"
										maxlines={3}
										noWrap>
										{skeleton ? <Skeleton width="100%" height="4rem" /> : <>{t(content)}</>}
									</Typography>
								</S.Content>
							</div>
							<S.Link>
								{skeleton ? (
									<Skeleton width="7rem" height="1.5rem" />
								) : (
									<Typography color="var(--blue) !important" fontWeight="500">
										{t('connect')}
										<Icon icon={faAngleRight} style={{ color: 'var(--blue)' }} />
									</Typography>
								)}
							</S.Link>
						</S.RightCol>
					</div>
				</Button>
			</S.Card>
		</>
	);
};

export default Card;
